import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import {
  MapPin, LogOut, User, Shield, Mountain,
  ChevronDown, MessageCircle, Compass, Download,
  Map, Bot, Radio, Sparkles,
} from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [hasInstallPrompt, setHasInstallPrompt] = useState(!!window.deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  );

  useEffect(() => {
    const handlePrompt = () => setHasInstallPrompt(true);
    const handleInstalled = () => setIsInstalled(true);
    
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/messages/conversations`, { withCredentials: true });
        if (isMounted) {
          const total = res.data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
          setUnreadCount(total);
        }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [user]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const isActive = (path) => location.pathname.startsWith(path);
  const getInitial = () => (user?.name || user?.username || "?").charAt(0).toUpperCase();

  const navLinks = [
    { to: "/places",     label: "Places",     icon: <MapPin size={16} /> },
    { to: "/channels",   label: "Channels",   icon: <Mountain size={16} /> },
    { to: "/trip-ai",    label: "Trip AI",    icon: <Map size={16} /> },
    { to: "/giri-ai",    label: "Giri AI",    icon: <Bot size={16} /> },
    { to: "/conditions", label: "Conditions", icon: <Radio size={16} /> },
    { to: "/festivals",  label: "Festivals",  icon: <Sparkles size={16} /> },
    { to: "/messages",   label: "Messages",   icon: <MessageCircle size={16} />, badge: unreadCount },
  ];

  const AvatarCircle = ({ size = 32 }) => {
    if (user?.avatar_path) {
      return (
        <img 
          src={user.avatar_path} 
          alt={user.name || user.username}
          className="rounded-full object-cover border-2 border-forest-green"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <div 
        className="rounded-full text-white flex items-center justify-center font-bold border-2 border-forest-green/50 shadow-sm shrink-0 bg-forest-green"
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {getInitial()}
      </div>
    );
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-card-border shadow-sm transition-all">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-warm bg-forest-green group-hover:scale-105 transition-transform">
              <Compass size={20} strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-xl tracking-tight hidden sm:block">
              <span className="text-text-dark font-display">Uttarakhand</span><span className="text-forest-green font-display">Travel</span>
            </span>
          </Link>

          {user && (
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link 
                  key={link.to} 
                  to={link.to} 
                  className={`relative px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all duration-200 group ${
                    isActive(link.to) 
                      ? "bg-forest-green/10 text-forest-green" 
                      : "text-text-secondary-dark hover:text-forest-green hover:bg-forest-green/5"
                  }`}
                >
                  {link.icon} 
                  {link.label}
                  {link.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                      {link.badge > 99 ? "99+" : link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 ml-auto">
            {user ? (
              <div className="relative">
                <button 
                  onClick={() => setDropdownOpen((v) => !v)}
                  className="flex items-center gap-2 p-1.5 rounded-full hover:bg-forest-green/5 transition-colors focus:outline-none"
                >
                  <AvatarCircle size={36} />
                  <span className="hidden md:block font-medium text-sm text-text-dark max-w-[120px] truncate">
                    {user.name || user.username}
                  </span>
                  <ChevronDown size={16} className={`hidden md:block text-text-secondary-dark transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {dropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-card-border rounded-2xl shadow-xl overflow-hidden z-50 py-2 animate-in fade-in slide-in-from-top-2">
                      <div className="px-4 py-2 border-b border-card-border md:hidden">
                        <p className="font-bold text-sm text-text-dark truncate">{user.name || user.username}</p>
                        <p className="text-xs text-text-secondary-dark truncate">{user.email}</p>
                      </div>
                      <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-dark hover:bg-forest-green/5 hover:text-forest-green transition-colors font-medium" onClick={() => setDropdownOpen(false)}>
                        <User size={16} /> Profile
                      </Link>
                      <Link to="/messages" className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-dark hover:bg-forest-green/5 hover:text-forest-green transition-colors font-medium" onClick={() => setDropdownOpen(false)}>
                        <MessageCircle size={16} /> Messages
                        {unreadCount > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{unreadCount}</span>}
                      </Link>
                      {user.role === "admin" && (
                        <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-dark hover:bg-forest-green/5 hover:text-forest-green transition-colors font-medium" onClick={() => setDropdownOpen(false)}>
                          <Shield size={16} /> Admin Dashboard
                        </Link>
                      )}
                      {!isInstalled && hasInstallPrompt && (
                        <button
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold transition-colors text-left"
                          style={{ color: '#40916C' }}
                          onClick={() => {
                            setDropdownOpen(false);
                            setShowInstallModal(true);
                          }}
                        >
                          <Download size={16} /> Install App
                        </button>
                      )}
                      <div className="h-px bg-card-border my-1" />
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium text-left"
                      >
                        <LogOut size={16} /> Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="px-3 sm:px-4 py-2 text-sm font-bold text-text-dark hover:text-forest-green transition-colors whitespace-nowrap">
                  Log in
                </Link>
                <Link to="/register" className="px-3 sm:px-4 py-2 text-sm font-bold text-white rounded-full transition-all hover:-translate-y-0.5 bg-forest-green shadow-warm hover:shadow-luxury whitespace-nowrap">
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Add to Home Screen Modal */}
      {showInstallModal && (
        <>
          <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 animate-in fade-in" onClick={() => setShowInstallModal(false)} />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 z-[60] shadow-2xl animate-in slide-in-from-bottom-8">
            <div className="w-12 h-1.5 bg-mist-200 rounded-full mx-auto mb-6" />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg bg-forest-green">
                <Compass size={32} className="text-white" />
              </div>
              <h3 className="font-display font-bold text-xl text-text-dark mb-1">
                Uttarakhand Travel
              </h3>
              <p className="text-text-secondary-dark text-sm">
                Install our app for quick access &amp; offline features.
              </p>
            </div>

            <button
              onClick={() => {
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                  window.deferredPrompt.userChoice.then(() => {
                    window.deferredPrompt = null;
                    setHasInstallPrompt(false);
                    setShowInstallModal(false);
                  });
                }
              }}
              className="w-full text-white bg-forest-green rounded-2xl py-3.5 font-bold shadow-lg hover:opacity-90 transition-all mb-3 text-lg"
            >
              Install App
            </button>
            <button
              onClick={() => setShowInstallModal(false)}
              className="w-full bg-transparent text-text-secondary-dark hover:bg-forest-green/5 rounded-2xl py-3.5 font-semibold transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </>
      )}
    </>
  );
}